import { useState, useEffect } from 'react'
import { useAuth, authenticatedFetch } from '../hooks/useAuth'
import { Link, useNavigate } from 'react-router-dom'
import DashboardLayout from '../components/DashboardLayout'
import { useI18n } from '../i18n/index.jsx'

// Central API base URL (Lambda)
const API_BASE = 'https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws'

export default function Account() {
  const { user, isAuthenticated, logout } = useAuth()
  const { t } = useI18n()
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState({ pseudo: '', nom: '', prenom: '', age: '' })
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateSuccess, setUpdateSuccess] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login')
      return
    }

    async function fetchUserInfo() {
      try {
        setLoading(true)
  const response = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/profile', { method: 'GET' })

        if (response.ok) {
          const data = await response.json()
          setUserInfo(data)
          setEditData({
            pseudo: data.pseudo || '',
            nom: data.nom || '',
            prenom: data.prenom || '',
            age: data.age || ''
          })
        } else {
          const errText = await response.text().catch(() => null)
          console.error('Failed to load profile:', response.status, errText)
          setError('Failed to load information')
        }
      } catch (err) {
        console.error('Account fetchUserInfo error:', err)
        setError(err?.message || 'Network error')
      } finally {
        setLoading(false)
      }
    }

    fetchUserInfo()
  }, [navigate]) // Supprimer isAuthenticated des dépendances

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPlanLabel = (planId) => {
    if (!planId || planId === 'free') return 'Gratuit'
    if (planId.includes('month') || planId.includes('19')) return 'Pro Mensuel'
    if (planId.includes('year') || planId.includes('99')) return 'Pro Annuel'
    if (planId.includes('39')) return 'Plus Mensuel'
    if (planId.includes('199')) return 'Plus Annuel'
    return 'Plan Premium'
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setUpdateLoading(true)
    setError('')
    setUpdateSuccess(false)

    try {
  const response = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/update_profile', {
        method: 'PATCH',
        body: JSON.stringify({
          pseudo: editData.pseudo,
          nom: editData.nom,
          prenom: editData.prenom,
          age: editData.age
        })
      })

      if (response.ok) {
        const data = await response.json()
        setUserInfo({ ...userInfo, ...data.updated })
        setUpdateSuccess(true)
        setEditing(false)
      } else {
        const errorData = await response.json().catch(() => null)
        console.error('Update profile failed:', response.status, errorData)
        setError(errorData?.detail || 'Erreur lors de la mise à jour')
      }
    } catch (err) {
      console.error('Account update error:', err)
      setError(err?.message || 'Erreur de connexion')
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value })
  }

  if (loading) {
  return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
      <p className="text-slate-400">{t('account.loading')}</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        {/* Glows décoratifs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
        <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

        <div className="relative z-10 py-20 px-6">
          <div className="mx-auto max-w-4xl">
            {/* Header */}
            <div className="mb-12 text-center">
              <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">{t('account.title')}</h1>
              <p className="mt-3 text-slate-400">{t('account.subtitle')}</p>
            </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-500/20 border border-red-500/30 p-4 text-center text-red-300">
              {error}
            </div>
          )}

          {updateSuccess && (
            <div className="mb-6 rounded-lg bg-emerald-500/20 border border-emerald-500/30 p-4 text-center text-emerald-300">{t('account.updateSuccess')}</div>
          )}

          {userInfo && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Informations personnelles */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-2xl">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-emerald-400">
                      <path fillRule="evenodd" d="M12 2.25a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9ZM3.75 20.1a8.25 8.25 0 1 1 16.5 0 .9.9 0 0 1-.9.9h-14.7a.9.9 0 0 1-.9-.9Z" clipRule="evenodd"/>
                    </svg>
                    {t('account.personalInfo')}
                  </h2>
                  <button
                    onClick={() => {
                      setEditing(!editing)
                      setError('')
                      setUpdateSuccess(false)
                    }}
                    className="text-sm text-emerald-400 hover:text-emerald-300 transition"
                  >
                    {editing ? t('account.cancel') : t('account.edit')}
                  </button>
                </div>
                
                {editing ? (
                  <form onSubmit={handleEditSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.pseudo')}</label>
                      <input
                        type="text"
                        name="pseudo"
                        value={editData.pseudo}
                        onChange={handleEditChange}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.nom')}</label>
                      <input
                        type="text"
                        name="nom"
                        value={editData.nom}
                        onChange={handleEditChange}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.prenom')}</label>
                      <input
                        type="text"
                        name="prenom"
                        value={editData.prenom}
                        onChange={handleEditChange}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.dob')}</label>
                      <input
                        type="date"
                        name="age"
                        value={editData.age}
                        onChange={handleEditChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={updateLoading}
                        className="flex-1 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400 transition disabled:bg-slate-600 disabled:text-slate-300"
                      >
                        {updateLoading ? t('account.saving') : t('account.save')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.pseudo')}</label>
                      <p className="mt-1 text-slate-100">{userInfo.pseudo || t('account.notProvided.masc')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.email')}</label>
                      <p className="mt-1 text-slate-100">{userInfo.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.nom')}</label>
                      <p className="mt-1 text-slate-100">{userInfo.nom || t('account.notProvided.masc')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.prenom')}</label>
                      <p className="mt-1 text-slate-100">{userInfo.prenom || t('account.notProvided.masc')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.labels.dob')}</label>
                      <p className="mt-1 text-slate-100">{userInfo.age ? new Date(userInfo.age).toLocaleDateString() : t('account.notProvided.fem')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Abonnement */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-2xl">
                <h2 className="mb-6 text-xl font-semibold flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-emerald-400">
                    <path d="M4.5 3.75A1.5 1.5 0 0 0 3 5.25v13.5A1.5 1.5 0 0 0 4.5 20.25h15A1.5 1.5 0 0 0 21 18.75V5.25A1.5 1.5 0 0 0 19.5 3.75H4.5ZM7.5 6.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5Z"/>
                  </svg>
                  {t('account.subscription.title')}
                </h2>
                
                {userInfo.subscription ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.subscription.currentPlan')}</label>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-medium text-emerald-300">
                          {getPlanLabel(userInfo.subscription.plan)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300">{t('account.subscription.endDate')}</label>
                      <p className="mt-1 text-slate-100">{formatDate(userInfo.subscription.date_fin)}</p>
                    </div>
                    <div className="pt-2">
                      <a
                        href="https://billing.stripe.com/p/login/test_eVqbJ1dw19vN7dB7eJeME00"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400 transition"
                      >
                        {t('account.subscription.manage')}
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-400 mb-4">{t('account.subscription.none')}</p>
                    <Link 
                      to="/pricing" 
                      className="inline-flex items-center rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400 transition"
                    >
                      {t('account.subscription.viewPlans')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 px-6 py-3 font-medium text-slate-100 hover:bg-white/10 transition"
            >
              {t('account.subscription.viewPlans')}
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center rounded-lg bg-red-500/20 border border-red-500/30 px-6 py-3 font-medium text-red-300 hover:bg-red-500/30 transition"
            >
              {t('account.logout')}
            </button>
          </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
